/** PRV Engine — Product Rotation + Validation (TMA admin only). Mirrors tma-be `src/prv`. */

export enum PrvSchedule {
  DAILY = "daily",
  WEEKLY = "weekly",
  BIWEEKLY = "biweekly",
  MONTHLY = "monthly",
  PAUSED = "paused",
}

export enum PrvRunStatus {
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
}

export enum PrvRunTrigger {
  SCHEDULE = "schedule",
  MANUAL = "manual",
}

export enum PrvRunStep {
  LOADING_CONFIG = "loading_config",
  READING_ROWS = "reading_rows",
  BUILDING_STAYS = "building_stays",
  SAVING = "saving",
}

export interface PrvRun {
  id: number;
  client?: { id: number; name: string };
  trigger: PrvRunTrigger;
  triggeredBy: { id: number; name: string; email: string } | null;
  status: PrvRunStatus;
  step: PrvRunStep | null;
  startedAt: string;
  finishedAt: string | null;
  rowsRead: number;
  itemsTouched: number;
  staysConfirmed: number;
  error: string | null;
  createdAt: string;
}

/** Per-client engine configuration, stored on the client record. */
export interface PrvConfig {
  prvSchedule: PrvSchedule;
  prvMinDwellMinutes: number;
}

export interface PrvSubClientAxis {
  id: number;
  name: string;
  hasCoordinates: boolean;
}

export interface PrvCategoryAxis {
  id: number;
  name: string;
  color: string | null;
}

/** Observed movement of one category at one sub-client. Aggregates only. */
export interface PrvPattern {
  /** Null only if the pattern row is missing underneath recorded item states (defensive). */
  id: number | null;
  subClientId: number;
  productId: number;
  /**
   * Stays: an item was at the sub-client, with an at-customer status, longer
   * than the min dwell. Counted whether or not it has left yet. Equals the
   * arrivals in the daily buckets.
   */
  stays: number;
  /** The subset of `stays` that has ended (item later seen elsewhere). */
  completedStays: number;
  /** Summed duration of the completed stays only. */
  totalStaySeconds: number;
  /** Arrival of the earliest stay. */
  firstMovementAt: string | null;
  /** Most recent arrival or departure. */
  lastMovementAt: string | null;
  /** Items in a recorded, still open stay at the sub-client, as of the last run. */
  present: number;
  /** The client's rotation definition for this pair, if any. */
  target: { days: number; count: number } | null;
}

export interface PrvPatternDay {
  patternId: number;
  /** UTC day, YYYY-MM-DD. */
  day: string;
  arrivals: number;
  departures: number;
}

/** Rows removed by a reset, per table. */
export interface PrvResetResult {
  runs: number;
  itemStates: number;
  patterns: number;
  /** Timestamped arrival / departure rows. */
  movements: number;
}

export interface PrvPatternsView {
  generatedAt: string;
  config: { schedule: PrvSchedule; radiusMeters: number; minDwellMinutes: number };
  lastRun: PrvRun | null;
  subClients: PrvSubClientAxis[];
  categories: PrvCategoryAxis[];
  patterns: PrvPattern[];
  days: PrvPatternDay[];
}
